import * as React from 'react';
import styled from 'styled-components';

import { Button } from 'components/bootstrap';
import { Icon, IconButton } from 'components/common';
import { Row } from 'common/components/layout-styles';
import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';
import type ElementConfigurationContainer from 'views/components/aggregationwizard/ElementConfigurationContainer';

import StepActionsButton from './StepActionsButton';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.spacings.xxs};
  border: 1px solid ${({ theme }) => theme.colors.misc.divider};
  background-color: ${({ theme }) => theme.colors.global.contentBackground};
  padding: 0.5rem;
  margin: 4px 0;
  height: 40px;
`;

const StyledButton = styled(Button)`
  border-radius: 4px;
`;

type Props = Omit<
  React.ComponentProps<typeof ElementConfigurationContainer>,
  'testIdPrefix' | 'onRemove' | 'elementTitle' | 'children'
> & {
  item: EventProcedureStepAPIType;
  onViewStep: (stepId: string) => void;
  onRemoveStep: (stepId: string) => void;
};

const StepListItem = React.forwardRef(
  (
    { item, onViewStep = () => {}, onRemoveStep = () => {}, dragHandleProps, draggableProps, className }: Props,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const handleViewStep = () => onViewStep(item?.id);
    const handleRemoveStep = () => onRemoveStep(item?.id);

    return (
      <Container ref={ref} className={className} {...(draggableProps ?? {})}>
        <Row $width="auto" style={{ overflow: 'hidden' }}>
          {dragHandleProps && (
            <Row
              $width="15px"
              $align="center"
              $justify="center"
              data-testid={`${item?.id}-drag-handle`}
              {...dragHandleProps}>
              <Icon size="sm" name="drag_indicator" />
            </Row>
          )}
          <div style={{ width: '90%', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {item?.title}
          </div>
        </Row>
        <Row $width="auto">
          <StepActionsButton step={item} />
          <StyledButton bsSize="xs" bsStyle="gray" onClick={handleViewStep} aria-label={`View ${item?.title}`}>
            View
          </StyledButton>
          <IconButton
            name="close"
            title="Remove"
            onClick={handleRemoveStep}
            aria-label={`Remove ${item?.title}`}
            size="sm"
          />
        </Row>
      </Container>
    );
  },
);

export default StepListItem;
